import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import ProjectImage from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-image';
import ProjectDate from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/project-date';
import EditDescription from '../../edit-description/edit-description';
import EditableCastAndCrew from '../../edit-cast-and-crew/editable-cast-and-crew';
import TagsSelect from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/tag-select/tags-select';
import AddNewSessionName from './add-new-session-name';
import BroadcastTypePicker from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker';
import SessionFeatures from '../../session-features/session-features';
import { getDefaultFeaturesByProjectType } from '../../session-features/session-features-helpers';
import useAddSessionModal from './use-add-session-modal';
import TranslateLangs from '@/utils/translate-langs.json';
import LanguagesSelector from '@ui/languages-selector/languages-selector';
import CategoriesSelect from '../../edit-session-categories/categories-select';
import Text from '@ui/text/text/text';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type AddNewSessionModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  navigateTo?: string;
};

const AddNewSessionModal = ({ open, setOpen, navigateTo }: AddNewSessionModalProps) => {
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [recalculateLanguageSelector, setRecalculateLanguageSelector] = useState(0);

  const handleCancel = () => setOpen(false);

  // ideally this would use react context and the necessary data would be directly accessed inside each child coponent
  // to prevent prop drilling and unnecessary re-renders, but that's a bit of a refactor and at the moment
  // this isn't slow so it's not a priority
  const {
    resetState,
    onImageUpload,
    handleClearImage,
    handleNameChange,
    handleDateChange,
    handleDescriptionBlur,
    handleAddSpeakers,
    handleAddCategories,
    onSave,
    checkIfSessionNameExists,
    isValid,
    savingSession,
    projectType,
    imageUrl,
    name,
    times,
    requiresTimeChange,
    description,
    selectedBroadcastType,
    selectedFeatures,
    speakers,
    displayTags,
    presetColors,
    setSelectedBroadcastType,
    setSelectedFeatures,
    setTags,
    setSelectedLanguages,
    defaultLanguage,
    setDefaultLanguage,
    selectedTimezone,
  } = useAddSessionModal({ handleCloseModal: handleCancel, navigateTo });

  const handleAfterOpen = useCallback(() => {
    setRecalculateLanguageSelector((prev) => prev + 1);
  }, []);

  return (
    <>
      <Modal
        open={open}
        fadeTransition={false}
        transitionDuration={400}
        onTransitionClosed={() => {
          resetState();
          setIsClosingModal(false);
        }}
        onTransitionClosing={() => setIsClosingModal(true)}
        onTransitionOpened={handleAfterOpen}
        size="full"
        withZoom={false}
        backgroundColor="var(--BG)"
        showBackdrop={false}
        data-component="AddNewSessionModal"
        RenderFooterContent={() => (
          <Container
            width="100%"
            maxWidth="992px"
            className="flex fd-row jc-flex-end ai-center padding-24"
            margin="0 auto"
          >
            <Container className="flex fd-row jc-flex-start ai-center gap-16">
              <Button onClick={handleCancel} design="LocalBttnSec" width="95px" height="48px" disabled={savingSession}>
                <Text className="L1" textAlign="center">
                  Cancel
                </Text>
              </Button>
              <Button
                backgroundColor={getThemeColorCSSVariable()}
                height="48px"
                width="81px"
                hoverOpacity={0.8}
                loading={savingSession}
                onClick={onSave}
                disabled={!isValid() || savingSession}
                className="flex ai-center jc-center"
              >
                <Text
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                  className="L1"
                >
                  Save
                </Text>
              </Button>
            </Container>
          </Container>
        )}
      >
        <Container width="992px" padding="0 24px" minWidth="992px" margin="0 auto" height="auto">
          <Container className="margin-t-56 margin-b-8" width="100%" maxWidth="992px">
            <Text tag="h2" className="H2">
              Add session
            </Text>
          </Container>

          <Container className="flex gap-24" width="100%">
            <Container className="flex-1 flex-column gap-16" maxWidth="392px">
              <ProjectImage
                width="392px"
                height="220px"
                onUpload={onImageUpload}
                img={imageUrl}
                onDelete={handleClearImage}
                disabled={savingSession}
              />

              <SessionFeatures
                projectTypeDefaultFeatures={getDefaultFeaturesByProjectType(projectType)}
                selectedFeatures={selectedFeatures}
                onChange={setSelectedFeatures}
              />
            </Container>

            <Container className="flex-1 flex-column gap-16" width="576px">
              <AddNewSessionName
                name={name}
                onDebounce={handleNameChange}
                onBlur={handleNameChange}
                /* adding `!isClosingModal` ensures we don't get the error border when we are in the middle of closing the modal */
                nameAlreadyExists={!isClosingModal && checkIfSessionNameExists()}
                savingSession={savingSession}
              />

              <Container className="flex gap-16">
                <Container width="432px" className="flex-column">
                  <ProjectDate
                    timestamp={times.timestamp}
                    end_timestamp={times.end_timestamp}
                    requiresTimeChange={requiresTimeChange}
                    disabled={savingSession}
                    selectedTimezone={selectedTimezone}
                    onChange={(data) => {
                      const { startDate, endDate, selectedTimezone: tz } = data;
                      if (startDate && endDate && startDate >= endDate) return;
                      handleDateChange({ startDate, endDate, selectedTimezone: tz });
                    }}
                  />
                </Container>
                <Container className="flex-column">
                  <BroadcastTypePicker
                    onChange={(selected) => {
                      setSelectedBroadcastType(selected);
                    }}
                    selected={selectedBroadcastType}
                  />
                </Container>
              </Container>

              <Container width="576px" className="flex ai-flex-start as-stretch gap-16">
                <Container width="278px" flex="1 1 0">
                  <CategoriesSelect
                    dynamicMenuPlacement
                    dynamicMenuPlacementOffset={-96} /* footer height */
                    defaultCategories={[]}
                    onMenuClose={handleAddCategories}
                    disabled={savingSession}
                  />
                </Container>

                <Container width="278px">
                  <TagsSelect
                    menuListMaxHeight={'220px'}
                    dynamicMenuPlacement
                    dynamicMenuPlacementOffset={-96} /* footer height */
                    tags={displayTags}
                    onMenuClose={(updatedTags) => {
                      setTags(updatedTags);
                    }}
                    onChange={(tags) => {
                      setTags(tags);
                    }}
                    presetColors={presetColors}
                    disabled={savingSession}
                  />
                </Container>
              </Container>

              <Container>
                <EditDescription
                  onBlur={handleDescriptionBlur}
                  defaultValue={description}
                  disabled={savingSession}
                  maxRowsFocused={3}
                />
              </Container>

              <EditableCastAndCrew
                defaultSpeakers={[]}
                onMenuClose={handleAddSpeakers}
                disabled={savingSession}
                speakers={speakers}
              />

              <Container>
                <LanguagesSelector
                  recalculateViewableItems={recalculateLanguageSelector}
                  dynamicMenuPlacement
                  dynamicMenuPlacementOffset={-96} /* footer height */
                  onMenuClose={setSelectedLanguages}
                  defaultLanguage={defaultLanguage}
                  isMulti={true}
                  hideBaseSelector={false}
                  onBaseSelect={(selected) => setDefaultLanguage(selected.value.toString())}
                  defaultSelectedLanguages={[
                    ...(defaultLanguage === 'en'
                      ? [{ value: 'en', label: 'English', meta: { value: 'en', label: 'English' } }]
                      : [
                          {
                            value: defaultLanguage,
                            label:
                              TranslateLangs.find((lang) => lang.code === defaultLanguage)?.label || defaultLanguage,
                            meta: { value: defaultLanguage, label: defaultLanguage },
                          },
                        ]),
                  ]}
                />
              </Container>
            </Container>
          </Container>
        </Container>
      </Modal>
    </>
  );
};

export default AddNewSessionModal;
